// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useMemo } from 'react';
import { createInstrumentKey, Instrument, InstrumentKey } from '../../utils/instrument';
import { useCNGetAllInstrumentConfigurations } from '../queries/useCNGetAllInstrumentConfigurations';
import { useCNGetRegistryAdminId } from '../queries/useCNGetRegistryAdminId';
import { useGetAllInstrumentConfigurations } from '../queries/useGetAllInstrumentConfigurations';

export type UseInstrumentsResultGetByKey = (key: InstrumentKey) => Instrument | null;

export type UseInstrumentsResultGetByKeys = (
  keys: InstrumentKey[] | Set<InstrumentKey>,
) => Instrument[];

export type UseInstrumentsResult = {
  instruments: Instrument[];
  getInstrumentByKey: UseInstrumentsResultGetByKey;
  getInstrumentsByKeys: UseInstrumentsResultGetByKeys;
};

export const useInstruments = (): UseInstrumentsResult => {
  const utilityInstruments = useGetAllInstrumentConfigurations();

  const cnAdminId = useCNGetRegistryAdminId();
  const cnInstruments = useCNGetAllInstrumentConfigurations();

  const data = useMemo(() => {
    const instruments: Instrument[] = [];
    const keyToInstrument: Map<InstrumentKey, Instrument> = new Map();

    const setInstrument = (id: string, admin: string, displayName: string) => {
      const key = createInstrumentKey(id, admin);

      const instrument: Instrument = {
        id,
        admin,
        displayName,
        key,
      };

      instruments.push(instrument);
      keyToInstrument.set(key, instrument);
    };

    if (utilityInstruments.data) {
      utilityInstruments.data.instrumentConfigurations.forEach(({ payload }) => {
        setInstrument(
          payload.defaultIdentifier.id,
          payload.defaultIdentifier.source,
          payload.defaultIdentifier.id,
        );
      });
    }

    if (cnAdminId.data && cnInstruments.data) {
      cnInstruments.data.forEach(({ id, name }) => {
        setInstrument(id, cnAdminId.data, `${id} (${name})`);
      });
    }

    return {
      instruments,
      keyToInstrument,
    };
  }, [utilityInstruments, cnAdminId, cnInstruments]);

  const getByKey: UseInstrumentsResultGetByKey = useCallback(
    (key) => {
      return data.keyToInstrument.get(key) ?? null;
    },
    [data],
  );

  const getByKeys: UseInstrumentsResultGetByKeys = useCallback(
    (keys) => {
      const { keyToInstrument } = data;

      const instruments: Instrument[] = [];

      keys.forEach((key) => {
        const instrument = keyToInstrument.get(key);
        if (instrument) {
          instruments.push(instrument);
        } else {
          console.warn(`Failed to get an instrument by key: ${key}.`);
        }
      });

      return instruments;
    },
    [data],
  );

  return {
    instruments: data.instruments,
    getInstrumentByKey: getByKey,
    getInstrumentsByKeys: getByKeys,
  };
};
